import { createPublicViewCache, publicViewContext } from './public-view-cache.js';
const PRODUCT_SELECTOR = '[data-product-section]';
const OFFER_SELECTOR = '[data-bundle-offer]';
const COMPONENT_SELECTOR = '[data-bundle-component]';
const GROUP_SELECTOR = '[data-bundle-group]';
const COMPONENT_CARD_SELECTOR = '[data-bundle-component-card]';
const CARD_LAYOUT_REGION_SELECTOR = '[data-bundle-card-layout-region]';
const ENHANCEMENT_CONTROL_SELECTOR = '[data-bundle-enhancement-control]';
const READY_NOW_REFRESH_REGION_SELECTOR = '[data-ready-now-refresh-region]';
const READY_NOW_CANDIDATE_SELECTOR = '[data-ready-now-candidate]';
const READY_NOW_REFRESH_SELECTOR = '[data-ready-now-refresh]';
const QUICK_VIEW_TRIGGER_SELECTOR = '[data-bundle-quick-view-trigger]';
const GOLD_COVER_SELECTOR = '[data-gold-cover][data-gold-cover-variant-id]';
const GOLD_COVER_SUBMITTER_SELECTOR = '[data-gold-cover-add]';
const GOLD_COVER_FALLBACK_OFFER_ID = 'gold-cover';
const GOLD_COVER_FALLBACK_OFFER_VERSION = 1;
const GOLD_COVER_GROUP_ID = 'gold-cover';
const QUICK_VIEW_SECTION_ID = 'bundle-quick-view';
const BUNDLE_DIALOG_EXIT_DURATION = 160;
const BUNDLE_DIALOG_CLOSE_BUFFER = 40;
const BUNDLE_OFFER_SCHEMA_V6 = 6;
const MAX_BUNDLE_DISCOUNT_RULES = 5;
const MAX_BUNDLE_DISCOUNT_TARGETS = 10;
const READY_NOW_BUNDLE_GROUP_ID = 'ready-now-coffee';
const quickViewCache = createPublicViewCache({ maxEntries: 4, maxBytes: 1_000_000 });
if (typeof window !== 'undefined') window.addEventListener('pagehide', () => quickViewCache.clear());
const bundleDiscountStates = new WeakMap();
const bundleDialogCloseTimers = new WeakMap();
const bundleCompactStates = new WeakMap();
const bundleLimitFeedbackStates = new WeakMap();
const STAGED_COMPONENT_SELECTOR = '[data-bundle-compact-stage-input]';
const BUNDLE_LIMIT_FEEDBACK_DURATION = 300;

const resetBundleDialogState = (dialog) => {
  const closeTimer = bundleDialogCloseTimers.get(dialog);
  if (closeTimer) window.clearTimeout(closeTimer);
  bundleDialogCloseTimers.delete(dialog);
  delete dialog.dataset.bundleDialogState;
};

const finishBundleDialogClose = (dialog) => {
  resetBundleDialogState(dialog);
  if (dialog.open) dialog.close();
};

const closeBundleDialog = (dialog) => {
  if (!dialog.open || dialog.dataset.bundleDialogState === 'closing') return;

  if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
    finishBundleDialogClose(dialog);
    return;
  }

  dialog.dataset.bundleDialogState = 'closing';
  bundleDialogCloseTimers.set(
    dialog,
    window.setTimeout(
      () => finishBundleDialogClose(dialog),
      BUNDLE_DIALOG_EXIT_DURATION + BUNDLE_DIALOG_CLOSE_BUFFER,
    ),
  );
};

const openBundleDialog = (dialog) => {
  resetBundleDialogState(dialog);
  if (!dialog.open) dialog.showModal();
};

const getOfferElement = (root) =>
  root.matches?.(OFFER_SELECTOR) ? root : root.querySelector(OFFER_SELECTOR);

export const setBundleEnhancementControlsEnabled = (offer, enabled) => {
  offer.querySelectorAll(ENHANCEMENT_CONTROL_SELECTOR).forEach((control) => {
    if (!(control instanceof HTMLInputElement) && !(control instanceof HTMLButtonElement)) return;
    const isAvailable = control.dataset.bundleControlAvailable === 'true';
    control.disabled = !enabled || !isAvailable;
  });
};

export const syncBundleCardRegionHeights = (region) => {
  const cards = [...region.querySelectorAll(COMPONENT_CARD_SELECTOR)]
    .filter((card) => card instanceof HTMLElement);

  if (cards.length < 2) return;

  cards.forEach((card) => card.style.removeProperty('--bundle-component-card-height'));
  const tallestCardHeight = Math.ceil(
    Math.max(...cards.map((card) => card.getBoundingClientRect().height)),
  );

  if (tallestCardHeight <= 0) return;

  cards.forEach((card) => {
    card.style.setProperty('--bundle-component-card-height', `${tallestCardHeight}px`);
  });
};

const initializeBundleCardLayout = (offer) => {
  const regions = [...offer.querySelectorAll(CARD_LAYOUT_REGION_SELECTOR)]
    .filter((region) => region instanceof HTMLElement);

  if (!regions.length) return;

  let layoutFrame = null;
  const scheduleSync = () => {
    if (layoutFrame !== null) window.cancelAnimationFrame(layoutFrame);
    layoutFrame = window.requestAnimationFrame(() => {
      regions.forEach(syncBundleCardRegionHeights);
      layoutFrame = null;
    });
  };

  scheduleSync();
  document.fonts?.ready.then(scheduleSync);

  if (typeof ResizeObserver === 'function') {
    const regionWidths = new WeakMap();
    const resizeObserver = new ResizeObserver((entries) => {
      const widthChanged = entries.some((entry) => {
        const previousWidth = regionWidths.get(entry.target);
        const nextWidth = entry.contentRect.width;
        regionWidths.set(entry.target, nextWidth);
        return previousWidth === undefined || previousWidth !== nextWidth;
      });

      if (widthChanged) scheduleSync();
    });

    regions.forEach((region) => resizeObserver.observe(region));
  }
};

const initializeReadyNowRefresh = (offer) => {
  const region = offer.querySelector(READY_NOW_REFRESH_REGION_SELECTOR);
  const refreshButton = offer.querySelector(READY_NOW_REFRESH_SELECTOR);
  const status = offer.querySelector('[data-ready-now-refresh-status]');

  if (
    !(region instanceof HTMLElement) ||
    !(refreshButton instanceof HTMLButtonElement) ||
    !(status instanceof HTMLElement)
  ) {
    return () => {};
  }

  const columns = [...region.querySelectorAll('[data-ready-now-column]')]
    .filter((column) => column instanceof HTMLElement);
  const candidates = [...region.querySelectorAll(READY_NOW_CANDIDATE_SELECTOR)]
    .filter((candidate) => candidate instanceof HTMLElement)
    .sort(
      (a, b) =>
        Number.parseInt(a.dataset.readyNowCandidateIndex || '0', 10) -
        Number.parseInt(b.dataset.readyNowCandidateIndex || '0', 10),
    );
  const visibleCount = normalizePositiveInteger(region.dataset.readyNowVisibleCount, columns.length);

  if (columns.length !== visibleCount || candidates.length <= visibleCount) {
    return () => {};
  }

  const pool = document.createElement('div');
  pool.hidden = true;
  pool.setAttribute('data-ready-now-candidate-pool', '');
  region.after(pool);
  candidates.filter((candidate) => candidate.hidden).forEach((candidate) => pool.append(candidate));
  let cursor = visibleCount % candidates.length;
  let refreshTurns = 0;
  let refreshing = false;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const getInput = (candidate) => candidate.querySelector(COMPONENT_SELECTOR);
  const getVisibleCandidates = () =>
    columns
      .map((column) => column.querySelector(READY_NOW_CANDIDATE_SELECTOR))
      .filter((candidate) => candidate instanceof HTMLElement);

  // Request the next small batch while the shopper is reading the current cards.
  const prepareImages = (cards, priority = 'low') => Promise.allSettled(
    cards.flatMap((card) => [...card.querySelectorAll('.coffee-product__bundle-component-image')])
      .map((image) => {
        image.fetchPriority = priority;
        image.loading = 'eager';
        return typeof image.decode === 'function' ? image.decode() : Promise.resolve();
      }),
  );
  const warmNextImages = () => {
    const visible = new Set(getVisibleCandidates());
    const upcoming = Array.from(
      { length: candidates.length },
      (_, offset) => candidates[(cursor + offset) % candidates.length],
    ).filter((card) => !visible.has(card)).slice(0, visibleCount);
    void prepareImages(upcoming);
  };
  void prepareImages(getVisibleCandidates(), 'auto');
  warmNextImages();

  const sync = () => {
    const visibleCandidates = getVisibleCandidates();
    const allVisibleSelected =
      visibleCandidates.length === visibleCount &&
      visibleCandidates.every((candidate) => getInput(candidate)?.checked === true);

    refreshButton.disabled = allVisibleSelected;
    status.textContent = allVisibleSelected
      ? 'All shown coffees are selected. Remove one to refresh your choices.'
      : '';
  };

  refreshButton.addEventListener('click', async () => {
    if (refreshing) return;
    const visibleCandidates = getVisibleCandidates();
    const selectedCandidates = new Set(
      visibleCandidates.filter((candidate) => getInput(candidate)?.checked === true),
    );
    const replaceableColumns = columns.filter((column) => {
      const candidate = column.querySelector(READY_NOW_CANDIDATE_SELECTOR);
      return candidate instanceof HTMLElement && !selectedCandidates.has(candidate);
    });

    if (!replaceableColumns.length) {
      sync();
      return;
    }

    const visibleCandidateSet = new Set(visibleCandidates);
    const orderedCandidates = Array.from(
      { length: candidates.length },
      (_, offset) => candidates[(cursor + offset) % candidates.length],
    ).filter((candidate) => !selectedCandidates.has(candidate));
    const replacements = [
      ...orderedCandidates.filter((candidate) => !visibleCandidateSet.has(candidate)),
      ...orderedCandidates.filter((candidate) => visibleCandidateSet.has(candidate)),
    ].slice(0, replaceableColumns.length);
    const lastReplacementIndex = candidates.indexOf(replacements.at(-1));
    if (lastReplacementIndex >= 0) cursor = (lastReplacementIndex + 1) % candidates.length;

    const currentCandidates = replaceableColumns.map((column) =>
      column.querySelector(READY_NOW_CANDIDATE_SELECTOR),
    );
    const animations = new Set();
    const stopMotion = () => animations.forEach((animation) => animation.cancel());
    const animateCards = async (cards, entering) => {
      if (reducedMotion.matches || typeof Element.prototype.animate !== 'function') return;
      await Promise.allSettled(cards.map((card, index) => {
        const animation = card.animate(
          entering
            ? [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'translateY(0)' }]
            : [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(-2px)' }],
          {
            duration: entering ? 180 : 100,
            delay: entering ? index * 25 : 0,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            fill: 'both',
          },
        );
        animations.add(animation);
        return animation.finished;
      }));
    };

    refreshing = true;
    refreshButton.setAttribute('aria-disabled', 'true');
    region.setAttribute('aria-busy', 'true');
    currentCandidates.forEach((card) => { card.inert = true; });
    reducedMotion.addEventListener('change', stopMotion);
    const icon = refreshButton.querySelector('.coffee-product__ready-now-refresh-icon');
    if (icon && !reducedMotion.matches) {
      refreshTurns += 1;
      icon.style.transform = `rotate(${refreshTurns * 360}deg)`;
    }

    try {
      // Keep the existing cards visible while an unusually slow thumbnail catches up.
      let imageDeadline;
      await Promise.race([
        prepareImages(replacements, 'auto'),
        new Promise((resolve) => { imageDeadline = window.setTimeout(resolve, 1200); }),
      ]);
      window.clearTimeout(imageDeadline);
      await animateCards(currentCandidates, false);
      currentCandidates.forEach((card) => {
        card.hidden = true;
        pool.append(card);
      });
      stopMotion();
      animations.clear();
      replaceableColumns.forEach((column, index) => {
        const replacement = replacements[index];
        replacement.hidden = false;
        replacement.inert = true;
        column.append(replacement);
      });
      syncBundleCardRegionHeights(region.closest(CARD_LAYOUT_REGION_SELECTOR) || region);
      await animateCards(replacements, true);
    } finally {
      stopMotion();
      reducedMotion.removeEventListener('change', stopMotion);
      [...currentCandidates, ...replacements].forEach((card) => { card.inert = false; });
      refreshing = false;
      refreshButton.removeAttribute('aria-disabled');
      region.removeAttribute('aria-busy');
      sync();
      if (!refreshButton.disabled) status.textContent = 'Coffee choices refreshed.';
      warmNextImages();
    }
  });

  return sync;
};

const normalizePositiveInteger = (value, fallback = 1) => {
  const parsed = Number.parseInt(String(value), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const normalizeVariantId = (value) => {
  const normalized = String(value || '').trim();

  if (!/^\d+$/.test(normalized)) {
    throw new TypeError('Bundle variant IDs must be numeric Shopify variant IDs.');
  }

  return normalized;
};

const normalizeOfferId = (value) => {
  const normalized = String(value || '').trim();

  if (!/^[a-z0-9][a-z0-9-]{2,79}$/.test(normalized)) {
    throw new TypeError('Bundle offer IDs must use lowercase letters, numbers, and hyphens.');
  }

  return normalized;
};

const normalizeGroupId = (value) => {
  const normalized = String(value || '').trim();

  if (!/^[a-z0-9][a-z0-9-]{1,47}$/.test(normalized)) {
    return 'add-ons';
  }

  return normalized;
};

const isPlainRecord = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const hasOnlyKeys = (value, allowedKeys) =>
  Object.keys(value).every((key) => allowedKeys.includes(key));

const isExactTrimmedString = (value, { maximum = 120, pattern } = {}) =>
  typeof value === 'string' &&
  value.length > 0 &&
  value.length <= maximum &&
  value === value.trim() &&
  (!pattern || pattern.test(value));

const parseDiscountRuleValue = (rule, shopCurrencyCode) => {
  if (rule.type === 'percentage') {
    if (
      !Number.isFinite(rule.value) ||
      rule.value <= 0 ||
      rule.value > 100 ||
      rule.value_minor_units !== 0 ||
      rule.currency_code !== null
    ) {
      return null;
    }

    return {
      type: 'percentage',
      value: rule.value,
      valueMinorUnits: 0,
      currencyCode: null,
    };
  }

  if (
    rule.type !== 'fixed_amount' ||
    rule.value !== 0 ||
    !Number.isInteger(rule.value_minor_units) ||
    rule.value_minor_units <= 0 ||
    rule.currency_code !== 'GBP' ||
    shopCurrencyCode !== 'GBP'
  ) {
    return null;
  }

  return {
    type: 'fixed_amount',
    value: 0,
    valueMinorUnits: rule.value_minor_units,
    currencyCode: 'GBP',
  };
};

export const parseBundleDiscountConfiguration = ({
  configuration,
  schemaVersion,
  schemaVersionSource,
  offerId,
  offerIdSource,
  offerVersion,
  offerVersionSource,
  parentProductId,
  shopCurrencyCode,
}) => {
  if (
    Number(schemaVersion) !== BUNDLE_OFFER_SCHEMA_V6 ||
    schemaVersionSource !== String(BUNDLE_OFFER_SCHEMA_V6) ||
    !isExactTrimmedString(offerId, {
      maximum: 80,
      pattern: /^[a-z0-9][a-z0-9-]{2,79}$/,
    }) ||
    offerIdSource !== offerId ||
    !/^[1-9]\d*$/.test(String(offerVersionSource || '')) ||
    !Number.isInteger(Number(offerVersion)) ||
    Number(offerVersion) < 1 ||
    Number(offerVersionSource) !== Number(offerVersion) ||
    !/^\d+$/.test(String(parentProductId || '')) ||
    !/^[A-Z]{3}$/.test(String(shopCurrencyCode || ''))
  ) {
    return null;
  }

  let source = configuration;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      return null;
    }
  }

  if (
    !isPlainRecord(source) ||
    !hasOnlyKeys(source, ['active', 'rules']) ||
    typeof source.active !== 'boolean' ||
    !Array.isArray(source.rules) ||
    source.rules.length > MAX_BUNDLE_DISCOUNT_RULES ||
    (source.active && source.rules.length < 1)
  ) {
    return null;
  }

  const seenRuleIds = new Set();
  const seenTargets = new Set();
  let componentTargetCount = 0;
  const rules = [];

  for (const rawRule of source.rules) {
    if (
      !isPlainRecord(rawRule) ||
      !hasOnlyKeys(rawRule, [
        'discount_id',
        'type',
        'value',
        'value_minor_units',
        'currency_code',
        'message',
        'scope',
      ]) ||
      !isExactTrimmedString(rawRule.discount_id, {
        maximum: 48,
        pattern: /^[a-z0-9][a-z0-9-]{1,47}$/,
      }) ||
      seenRuleIds.has(rawRule.discount_id) ||
      !isExactTrimmedString(rawRule.message, { maximum: 120 }) ||
      !isPlainRecord(rawRule.scope)
    ) {
      return null;
    }

    const value = parseDiscountRuleValue(rawRule, shopCurrencyCode);
    if (!value) return null;
    seenRuleIds.add(rawRule.discount_id);

    let scope;
    if (rawRule.scope.kind === 'coffee_upsell') {
      if (
        !hasOnlyKeys(rawRule.scope, ['kind', 'max_discounted_quantity']) ||
        rawRule.scope.max_discounted_quantity !== 1 ||
        seenTargets.has('coffee_upsell')
      ) {
        return null;
      }
      seenTargets.add('coffee_upsell');
      scope = { kind: 'coffee_upsell', maxDiscountedQuantity: 1 };
    } else if (rawRule.scope.kind === 'components') {
      if (
        rawRule.discount_id === 'coffee-upsell' ||
        !hasOnlyKeys(rawRule.scope, ['kind', 'targets']) ||
        !Array.isArray(rawRule.scope.targets) ||
        rawRule.scope.targets.length < 1
      ) {
        return null;
      }

      componentTargetCount += rawRule.scope.targets.length;
      if (componentTargetCount > MAX_BUNDLE_DISCOUNT_TARGETS) return null;
      const targets = [];

      for (const rawTarget of rawRule.scope.targets) {
        if (
          !isPlainRecord(rawTarget) ||
          !hasOnlyKeys(rawTarget, [
            'component_variant_id',
            'group_id',
            'sku',
            'quantity',
            'quantity_mode',
            'max_discounted_quantity',
          ]) ||
          !isExactTrimmedString(rawTarget.component_variant_id, {
            maximum: 32,
            pattern: /^\d+$/,
          }) ||
          !isExactTrimmedString(rawTarget.group_id, {
            maximum: 48,
            pattern: /^[a-z0-9][a-z0-9-]{1,47}$/,
          }) ||
          !isExactTrimmedString(rawTarget.sku, { maximum: 100 }) ||
          !Number.isInteger(rawTarget.quantity) ||
          rawTarget.quantity < 1 ||
          rawTarget.quantity > 100 ||
          (rawTarget.quantity_mode !== 'fixed' && rawTarget.quantity_mode !== 'per_parent') ||
          rawTarget.max_discounted_quantity !== 1 ||
          seenTargets.has(`component:${rawTarget.component_variant_id}`)
        ) {
          return null;
        }

        seenTargets.add(`component:${rawTarget.component_variant_id}`);
        targets.push({
          variantId: rawTarget.component_variant_id,
          groupId: rawTarget.group_id,
          sku: rawTarget.sku,
          quantity: rawTarget.quantity,
          quantityMode: rawTarget.quantity_mode,
          maxDiscountedQuantity: 1,
        });
      }

      scope = { kind: 'components', targets };
    } else {
      return null;
    }

    rules.push({
      discountId: rawRule.discount_id,
      message: rawRule.message,
      ...value,
      scope,
    });
  }

  return { active: source.active, rules };
};

export const resolveBundleDiscountMatches = ({
  configuration,
  components,
  coffeeMaxSelections,
}) => {
  if (!configuration || !Array.isArray(configuration.rules) || !Array.isArray(components)) {
    return null;
  }
  if (!configuration.active) return new Map();

  const matches = new Map();
  for (const rule of configuration.rules) {
    if (rule.scope.kind === 'coffee_upsell') {
      if (Number(coffeeMaxSelections) !== 1) return null;
      const coffeeComponents = components.filter((component) => component.coffeeGoverned === true);
      const seenCoffeeVariantIds = new Set();

      for (const component of coffeeComponents) {
        if (
          !/^\d+$/.test(component.variantId) ||
          !isExactTrimmedString(component.sku, { maximum: 100 }) ||
          component.groupId !== READY_NOW_BUNDLE_GROUP_ID ||
          component.quantity !== 1 ||
          component.quantityMode !== 'fixed' ||
          seenCoffeeVariantIds.has(component.variantId) ||
          matches.has(component.variantId)
        ) {
          return null;
        }
        seenCoffeeVariantIds.add(component.variantId);
        matches.set(component.variantId, {
          ...rule,
          targetKind: 'coffee_upsell',
          maxDiscountedQuantity: 1,
          usageKey: `${rule.discountId}:coffee_upsell`,
        });
      }
      continue;
    }

    for (const target of rule.scope.targets) {
      const variantCandidates = components.filter(
        (component) => component.variantId === target.variantId,
      );
      if (variantCandidates.length !== 1) return null;
      const component = variantCandidates[0];
      if (
        component.eligibilityValidated !== true ||
        component.sku !== target.sku ||
        component.groupId !== target.groupId ||
        component.quantity !== target.quantity ||
        component.quantityMode !== target.quantityMode ||
        matches.has(component.variantId)
      ) {
        return null;
      }

      matches.set(component.variantId, {
        ...rule,
        targetKind: 'component',
        maxDiscountedQuantity: target.maxDiscountedQuantity,
        usageKey: `${rule.discountId}:component:${component.variantId}`,
      });
    }
  }

  return matches;
};

export const resolveSelectedBundleDiscountMatches = ({ matches, selectedVariantIds }) => {
  if (!(matches instanceof Map) || !Array.isArray(selectedVariantIds)) return new Map();

  const selected = new Map();
  const coffeeUsageCounts = new Map();
  for (const variantId of selectedVariantIds.map(String)) {
    const match = matches.get(variantId);
    if (!match) continue;
    selected.set(variantId, match);
    if (match.targetKind === 'coffee_upsell') {
      coffeeUsageCounts.set(match.usageKey, (coffeeUsageCounts.get(match.usageKey) || 0) + 1);
    }
  }

  for (const [variantId, match] of selected) {
    if (
      match.targetKind === 'coffee_upsell' &&
      (coffeeUsageCounts.get(match.usageKey) || 0) !== 1
    ) {
      selected.delete(variantId);
    }
  }
  return selected;
};

export const reconcileGroupSelection = ({ selectedIds, changedId, maxSelections }) => {
  const normalizedIds = [...new Set((selectedIds || []).map(String))];
  const maximum = normalizePositiveInteger(maxSelections);
  const normalizedChangedId = String(changedId || '');

  if (normalizedIds.length <= maximum) {
    return { accepted: true, selectedIds: normalizedIds };
  }

  return {
    accepted: false,
    selectedIds: normalizedIds.filter((id) => id !== normalizedChangedId).slice(0, maximum),
  };
};

const normalizeProperties = (properties = {}) =>
  Object.fromEntries(
    Object.entries(properties)
      .filter(([key, value]) => key && value !== undefined && value !== null && value !== '')
      .map(([key, value]) => [String(key), String(value)]),
  );

export const createNestedCartItems = ({
  parentVariantId,
  parentQuantity,
  parentSellingPlanId = '',
  offerId,
  offerVersion = 1,
  instanceId,
  components,
  parentProperties = {},
}) => {
  const normalizedParentVariantId = normalizeVariantId(parentVariantId);
  const normalizedParentQuantity = normalizePositiveInteger(parentQuantity);
  const normalizedParentSellingPlanId = String(parentSellingPlanId || '').trim();
  const normalizedOfferId = normalizeOfferId(offerId);
  const normalizedOfferVersion = normalizePositiveInteger(offerVersion);
  const normalizedInstanceId = String(instanceId || '').trim();

  if (normalizedParentSellingPlanId && !/^[1-9]\d*$/.test(normalizedParentSellingPlanId)) {
    throw new TypeError('The selected purchase option does not have a valid selling plan.');
  }

  if (!normalizedInstanceId || normalizedInstanceId.length > 100) {
    throw new TypeError('A bundle instance ID between 1 and 100 characters is required.');
  }

  if (!Array.isArray(components) || components.length === 0) {
    throw new TypeError('At least one bundle component is required.');
  }

  const seenVariantIds = new Set();
  const sharedProperties = {
    _sigma_offer_id: normalizedOfferId,
    _sigma_offer_version: String(normalizedOfferVersion),
    _sigma_bundle_instance: normalizedInstanceId,
  };

  const parentItem = {
    id: normalizedParentVariantId,
    quantity: normalizedParentQuantity,
    ...(normalizedParentSellingPlanId ? { selling_plan: normalizedParentSellingPlanId } : {}),
    properties: {
      ...normalizeProperties(parentProperties),
      ...sharedProperties,
      _sigma_bundle_role: 'parent',
    },
  };

  const childItems = components.map((component) => {
    const variantId = normalizeVariantId(component.variantId);

    if (variantId === normalizedParentVariantId) {
      throw new TypeError('A nested add-on cannot use the same variant as its parent.');
    }

    if (seenVariantIds.has(variantId)) {
      throw new TypeError(`Duplicate bundle component variant: ${variantId}.`);
    }
    seenVariantIds.add(variantId);

    const componentQuantity = normalizePositiveInteger(component.quantity);
    const quantityMode = component.quantityMode === 'fixed' ? 'fixed' : 'per_parent';
    const groupId = normalizeGroupId(component.groupId);
    const sellingPlanId = String(component.sellingPlanId || '').trim();

    if (sellingPlanId && !/^[1-9]\d*$/.test(sellingPlanId)) {
      throw new TypeError('Bundle selling plan IDs must be positive numeric Shopify IDs.');
    }

    const cartQuantity =
      quantityMode === 'fixed'
        ? componentQuantity
        : componentQuantity * normalizedParentQuantity;

    return {
      id: variantId,
      quantity: cartQuantity,
      parent_id: normalizedParentVariantId,
      ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
      properties: {
        ...sharedProperties,
        _sigma_bundle_role: 'add_on',
        _sigma_parent_variant_id: normalizedParentVariantId,
        _sigma_component_quantity: String(componentQuantity),
        _sigma_quantity_mode: quantityMode,
        _sigma_bundle_group: groupId,
        ...(component.purchaseSource === 'ready_now'
          ? { _sigma_purchase_source: 'ready_now', _sigma_purchase_placement: 'product_add_on' }
          : {}),
        ...(component.goldCoverTermsAccepted === true
          ? { _sigma_gold_cover_terms: 'accepted' }
          : {}),
      },
    };
  });

  return [parentItem, ...childItems];
};

const createBundleInstanceId = () => {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  if (globalThis.crypto?.getRandomValues) {
    const bytes = new Uint32Array(4);
    globalThis.crypto.getRandomValues(bytes);
    return [...bytes].map((value) => value.toString(16).padStart(8, '0')).join('-');
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getFormProperties = (formData) => {
  const properties = {};

  for (const [key, value] of formData.entries()) {
    const propertyMatch = key.match(/^properties\[(.+)]$/);
    if (propertyMatch && typeof value === 'string' && value) {
      properties[propertyMatch[1]] = value;
    }
  }

  return properties;
};

const getSelectedOffer = (form) => {
  const selectedInputs = [...form.elements]
    .filter((element) => element.matches?.(`${COMPONENT_SELECTOR}:checked:not(:disabled)`))
    .filter((input) => input instanceof HTMLInputElement);

  if (!selectedInputs.length) {
    return null;
  }

  const offer = selectedInputs[0].closest(OFFER_SELECTOR);
  if (!(offer instanceof HTMLElement)) {
    return null;
  }

  if (selectedInputs.some((input) => input.closest(OFFER_SELECTOR) !== offer)) {
    throw new TypeError('Selected add-ons must belong to the same offer.');
  }

  return {
    element: offer,
    components: selectedInputs.map((input) => ({
      variantId: input.dataset.bundleVariantId,
      quantity: normalizePositiveInteger(input.dataset.bundleComponentQuantity),
      quantityMode: input.dataset.bundleQuantityMode === 'fixed' ? 'fixed' : 'per_parent',
      priceMinorUnits: Number.parseInt(input.dataset.bundlePriceMinorUnits || '', 10),
      groupId: normalizeGroupId(input.dataset.bundleGroupId),
      purchaseSource: input.dataset.bundlePurchaseSource === 'ready_now' ? 'ready_now' : '',
    })),
  };
};

const getSelectedSellingPlanId = (formData) => {
  const selectedPlans = formData.getAll('selling_plan')
    .filter((sellingPlan) => typeof sellingPlan !== 'string' || sellingPlan.trim() !== '');

  if (selectedPlans.length === 0) return '';
  if (
    selectedPlans.length !== 1
    || typeof selectedPlans[0] !== 'string'
    || !/^[1-9]\d*$/.test(selectedPlans[0].trim())
  ) {
    throw new TypeError('The selected purchase option does not have a valid selling plan.');
  }

  return selectedPlans[0].trim();
};

const selectedParentRequiresSellingPlan = (form) => {
  const variantControl = form.querySelector?.('[data-variant-select]');
  const selectedOption = variantControl?.selectedOptions?.[0];
  return (selectedOption?.dataset.requiresSellingPlan || variantControl?.dataset.requiresSellingPlan) === 'true';
};

const getGoldCoverComponent = (form, submitter) => {
  if (!(submitter instanceof HTMLElement) || !submitter.matches(GOLD_COVER_SUBMITTER_SELECTOR)) {
    return null;
  }

  const goldCover = submitter.closest(GOLD_COVER_SELECTOR);
  const productRoot = form.closest(PRODUCT_SELECTOR);
  if (
    !(goldCover instanceof HTMLElement)
    || !(productRoot instanceof HTMLElement)
    || goldCover.closest(PRODUCT_SELECTOR) !== productRoot
    || goldCover.dataset.goldCoverFormId !== form.getAttribute('id')
    || submitter.form !== form
    || goldCover.dataset.goldCoverAvailable !== 'true'
  ) {
    throw new TypeError('The Gold Cover offer is unavailable.');
  }

  const sellingPlanId = String(goldCover.dataset.goldCoverSellingPlanId || '').trim();
  const requiresSellingPlan = goldCover.dataset.goldCoverRequiresSellingPlan === 'true';
  const termsRequired = goldCover.dataset.goldCoverTermsRequired === 'true';
  const terms = productRoot.querySelector('[data-gold-cover-terms]');

  if ((requiresSellingPlan || sellingPlanId) && !/^[1-9]\d*$/.test(sellingPlanId)) {
    throw new TypeError('The Gold Cover offer does not have a valid selling plan.');
  }

  if (
    termsRequired
    && (
      !(terms instanceof HTMLInputElement)
      || terms.type !== 'checkbox'
      || terms.form !== form
      || !terms.checked
    )
  ) {
    terms?.focus();
    throw new TypeError('Agree to the Gold Cover terms before adding cover.');
  }

  return {
    variantId: goldCover.dataset.goldCoverVariantId,
    quantity: 1,
    quantityMode: 'per_parent',
    priceMinorUnits: Number.parseInt(goldCover.dataset.goldCoverPriceMinorUnits || '', 10),
    groupId: GOLD_COVER_GROUP_ID,
    sellingPlanId,
    goldCoverTermsAccepted: termsRequired && terms.checked,
  };
};

export const buildBundleCartAddRequest = (
  form,
  formData,
  { sections, sectionsUrl, instanceId = createBundleInstanceId(), submitter } = {},
) => {
  const selectedOffer = getSelectedOffer(form);
  const goldCoverComponent = getGoldCoverComponent(form, submitter);

  if (!selectedOffer && !goldCoverComponent) {
    return null;
  }

  const parentSellingPlanId = getSelectedSellingPlanId(formData);
  const parentRequiresSellingPlan = selectedParentRequiresSellingPlan(form);
  const preorderActive = form.dataset.stoqPreorderActive === 'true';
  const parentUsesSellingPlan = Boolean(
    parentSellingPlanId || parentRequiresSellingPlan || preorderActive,
  );

  if (parentUsesSellingPlan && (!goldCoverComponent || selectedOffer)) {
    throw new TypeError('Bundle add-ons are available with a one-time purchase only.');
  }

  if ((parentRequiresSellingPlan || preorderActive) && !parentSellingPlanId) {
    throw new TypeError('Select the preorder purchase option before adding Gold Cover.');
  }

  const components = [];
  const componentIndexes = new Map();

  [...(selectedOffer?.components || []), ...(goldCoverComponent ? [goldCoverComponent] : [])]
    .forEach((component) => {
      const variantId = String(component.variantId || '').trim();
      const existingIndex = componentIndexes.get(variantId);

      if (existingIndex !== undefined) {
        components[existingIndex] = { ...components[existingIndex], ...component };
        return;
      }

      componentIndexes.set(variantId, components.length);
      components.push(component);
    });

  const parentVariantId = formData.get('id');
  const parentQuantity = normalizePositiveInteger(formData.get('quantity'));
  const items = createNestedCartItems({
    parentVariantId,
    parentQuantity,
    parentSellingPlanId,
    offerId: selectedOffer?.element.dataset.bundleOfferId || GOLD_COVER_FALLBACK_OFFER_ID,
    offerVersion: selectedOffer?.element.dataset.bundleOfferVersion || GOLD_COVER_FALLBACK_OFFER_VERSION,
    instanceId,
    components,
    parentProperties: getFormProperties(formData),
  });

  return {
    childCount: items.length - 1,
    payload: {
      items,
      ...(sections ? { sections } : {}),
      ...(sectionsUrl ? { sections_url: sectionsUrl } : {}),
    },
  };
};

const parseVariants = (root) => {
  const source = root.querySelector('[data-product-variants]');

  if (!source) {
    return [];
  }

  try {
    return JSON.parse(source.textContent || '[]');
  } catch {
    return [];
  }
};

const getCurrentVariant = (root) => {
  const variantId = root.querySelector('[data-variant-select]')?.value;
  return parseVariants(root).find((variant) => String(variant.id) === String(variantId));
};

const getDiscountedUnitPrice = ({
  priceMinorUnits,
  type,
  value,
  valueMinorUnits,
  currencyCode,
  shopCurrencyCode,
}) => {
  const price = Number(priceMinorUnits);
  if (!Number.isFinite(price) || price < 0) {
    return null;
  }

  if (type === 'percentage') {
    const percentage = Number(value);
    if (!Number.isFinite(percentage) || percentage <= 0 || percentage > 100) {
      return null;
    }
    return Math.round(price * (1 - percentage / 100));
  }

  if (type === 'fixed_amount') {
    const reduction = Number(valueMinorUnits);
    if (
      !Number.isInteger(reduction) ||
      reduction <= 0 ||
      !currencyCode ||
      currencyCode !== shopCurrencyCode
    ) {
      return null;
    }
    return Math.max(0, price - reduction);
  }

  return null;
};

export const calculateBundleRuleComponentTotal = ({
  priceMinorUnits,
  quantity,
  match,
  shopCurrencyCode,
  discountedQuantityUsed = 0,
}) => {
  const unitPrice = Number(priceMinorUnits);
  const normalizedQuantity = normalizePositiveInteger(quantity);
  const fullPriceTotal = Number.isFinite(unitPrice) && unitPrice >= 0
    ? unitPrice * normalizedQuantity
    : 0;

  if (
    !match ||
    match.maxDiscountedQuantity !== 1 ||
    !Number.isInteger(discountedQuantityUsed) ||
    discountedQuantityUsed < 0 ||
    discountedQuantityUsed >= 1
  ) {
    return {
      totalMinorUnits: fullPriceTotal,
      discountedQuantity: 0,
      discountedUnitPrice: null,
    };
  }

  const discountedUnitPrice = getDiscountedUnitPrice({
    priceMinorUnits: unitPrice,
    type: match.type,
    value: match.value,
    valueMinorUnits: match.valueMinorUnits,
    currencyCode: match.currencyCode,
    shopCurrencyCode,
  });
  if (discountedUnitPrice === null || discountedUnitPrice >= unitPrice) {
    return {
      totalMinorUnits: fullPriceTotal,
      discountedQuantity: 0,
      discountedUnitPrice: null,
    };
  }

  return {
    totalMinorUnits: discountedUnitPrice + unitPrice * (normalizedQuantity - 1),
    discountedQuantity: 1,
    discountedUnitPrice,
  };
};

export const calculateScopedBundleComponentTotal = ({
  priceMinorUnits,
  quantity,
  groupId,
  componentDiscountScope,
  discountActive,
  discountScope,
  discountType,
  discountValue,
  discountValueMinorUnits,
  discountCurrencyCode,
  shopCurrencyCode,
  maxDiscountedQuantity,
  discountedQuantityUsed = 0,
}) => {
  const unitPrice = Math.max(0, Number(priceMinorUnits) || 0);
  const normalizedQuantity = normalizePositiveInteger(quantity);
  const fullPriceTotal = unitPrice * normalizedQuantity;
  const maximum = Number.parseInt(String(maxDiscountedQuantity), 10);
  const alreadyDiscounted = Math.max(0, Number.parseInt(String(discountedQuantityUsed), 10) || 0);

  if (
    discountActive !== true ||
    discountScope !== 'coffee_upsell' ||
    componentDiscountScope !== 'coffee_upsell' ||
    groupId !== 'ready-now-coffee' ||
    maximum !== 1 ||
    alreadyDiscounted >= maximum
  ) {
    return { totalMinorUnits: fullPriceTotal, discountedQuantity: 0 };
  }

  const discountedUnitPrice = getDiscountedUnitPrice({
    priceMinorUnits: unitPrice,
    type: discountType,
    value: discountValue,
    valueMinorUnits: discountValueMinorUnits,
    currencyCode: discountCurrencyCode,
    shopCurrencyCode,
  });

  if (discountedUnitPrice === null) {
    return { totalMinorUnits: fullPriceTotal, discountedQuantity: 0 };
  }

  const discountedQuantity = Math.min(normalizedQuantity, maximum - alreadyDiscounted);
  return {
    totalMinorUnits:
      discountedUnitPrice * discountedQuantity +
      unitPrice * (normalizedQuantity - discountedQuantity),
    discountedQuantity,
  };
};

const formatMoney = (root, amountMinorUnits) => {
  try {
    return new Intl.NumberFormat(root.dataset.locale || document.documentElement.lang || 'en-GB', {
      style: 'currency',
      currency: root.dataset.currencyCode || 'GBP',
      currencyDisplay: 'narrowSymbol',
    }).format(amountMinorUnits / 100);
  } catch {
    return `£${(amountMinorUnits / 100).toFixed(2)}`;
  }
};

const getBundleComponentDescriptors = (offer) =>
  [...offer.querySelectorAll(COMPONENT_SELECTOR)]
    .filter((input) => input instanceof HTMLInputElement)
    .map((input) => ({
      input,
      variantId: String(input.dataset.bundleVariantId || ''),
      sku: String(input.dataset.bundleSku || ''),
      groupId: String(input.dataset.bundleGroupId || ''),
      quantity: Number(input.dataset.bundleComponentQuantity),
      quantityMode: input.dataset.bundleQuantityMode,
      eligibilityValidated: input.dataset.bundleEligibilityValidated === 'true',
      coffeeGoverned: input.dataset.bundleCoffeeGoverned === 'true',
      available: input.dataset.bundleControlAvailable === 'true',
    }));

const resetBundleDiscountCard = (card) => {
  const price = card.querySelector('[data-bundle-component-price]');
  const retailPrice = card.querySelector('[data-bundle-retail-price]');
  const discountedPrice = card.querySelector('[data-bundle-discounted-price]');
  const accessiblePrice = card.querySelector('[data-bundle-discount-price-a11y]');
  const message = card.querySelector('[data-bundle-discount-message]');

  card.classList.remove('has-bundle-discount');
  price?.classList.remove('is-discounted');
  retailPrice?.removeAttribute('aria-hidden');
  if (discountedPrice) {
    discountedPrice.hidden = true;
    discountedPrice.textContent = '';
    discountedPrice.removeAttribute('aria-hidden');
  }
  if (accessiblePrice) {
    accessiblePrice.hidden = true;
    accessiblePrice.textContent = '';
  }
  if (message) {
    message.hidden = true;
    message.textContent = '';
    message.removeAttribute('aria-hidden');
  }
};

const applyBundleDiscountCard = ({ root, component, match }) => {
  const card = component.input.closest(COMPONENT_CARD_SELECTOR);
  if (!(card instanceof HTMLElement) || !component.available) return;

  const priceMinorUnits = Number(component.input.dataset.bundlePriceMinorUnits);
  const pricing = calculateBundleRuleComponentTotal({
    priceMinorUnits,
    quantity: component.quantity,
    match,
    shopCurrencyCode: root.dataset.currencyCode,
  });
  if (pricing.discountedQuantity !== 1) return;

  const retailPrice = card.querySelector('[data-bundle-retail-price]');
  const discountedPrice = card.querySelector('[data-bundle-discounted-price]');
  const accessiblePrice = card.querySelector('[data-bundle-discount-price-a11y]');
  const message = card.querySelector('[data-bundle-discount-message]');
  const price = card.querySelector('[data-bundle-component-price]');
  if (!retailPrice || !discountedPrice || !accessiblePrice || !message || !price) return;

  const retailTotal = priceMinorUnits * component.quantity;
  const formattedRetail = `+${formatMoney(root, retailTotal)}`;
  const formattedDiscounted = `+${formatMoney(root, pricing.totalMinorUnits)}`;
  const formattedSaving = formatMoney(root, retailTotal - pricing.totalMinorUnits);


  card.classList.add('has-bundle-discount');
  price.classList.add('is-discounted');
  retailPrice.textContent = formattedRetail;
  retailPrice.setAttribute('aria-hidden', 'true');
  discountedPrice.hidden = false;
  discountedPrice.textContent = formattedDiscounted;
  discountedPrice.setAttribute('aria-hidden', 'true');
  accessiblePrice.hidden = false;
  accessiblePrice.textContent = `Retail ${formattedRetail}. Bundle price ${formattedDiscounted}. Save ${formattedSaving}.`;
  message.hidden = false;
  message.textContent = `Save ${formattedSaving}`;
  message.setAttribute('aria-hidden', 'true');
};

const initializeBundleDiscountPresentation = (root, offer) => {
  const schemaVersion = Number(offer.dataset.bundleOfferSchemaVersion);
  if (schemaVersion !== BUNDLE_OFFER_SCHEMA_V6) {
    bundleDiscountStates.set(offer, { schemaVersion, matches: new Map(), valid: true });
    return;
  }

  const components = getBundleComponentDescriptors(offer);
  components.forEach(({ input }) => {
    const card = input.closest(COMPONENT_CARD_SELECTOR);
    if (card instanceof HTMLElement) resetBundleDiscountCard(card);
  });

  const configuration = parseBundleDiscountConfiguration({
    configuration: offer.dataset.bundleDiscountConfiguration,
    schemaVersion,
    schemaVersionSource: offer.dataset.bundleOfferSchemaVersionSource,
    offerId: offer.dataset.bundleOfferId,
    offerIdSource: offer.dataset.bundleOfferIdSource,
    offerVersion: Number(offer.dataset.bundleOfferVersion),
    offerVersionSource: offer.dataset.bundleOfferVersionSource,
    parentProductId: offer.dataset.bundleParentProductId,
    shopCurrencyCode: root.dataset.currencyCode,
  });
  const matches = resolveBundleDiscountMatches({
    configuration,
    components,
    coffeeMaxSelections: Number(offer.dataset.bundleCoffeeMaxSelections),
  });

  if (!configuration || !matches) {
    bundleDiscountStates.set(offer, { schemaVersion, matches: new Map(), valid: false });
    return;
  }

  bundleDiscountStates.set(offer, { schemaVersion, matches, valid: true });
  components.forEach((component) => {
    const match = matches.get(component.variantId);
    if (match) applyBundleDiscountCard({ root, component, match });
  });
};

const calculateBundleSelectionTotals = ({ root, offer, selectedInputs, parentQuantity }) => {
  let componentTotalMinorUnits = 0;
  const groupTotals = new Map();
  let discountedQuantityUsed = 0;
  const discountState = bundleDiscountStates.get(offer);
  const v6DiscountUsage = new Map();
  const selectedV6Matches = resolveSelectedBundleDiscountMatches({
    matches: discountState?.valid ? discountState.matches : new Map(),
    selectedVariantIds: selectedInputs.map((input) => input.dataset.bundleVariantId),
  });

  selectedInputs.forEach((input) => {
    const componentQuantity = normalizePositiveInteger(input.dataset.bundleComponentQuantity);
    const cartQuantity =
      input.dataset.bundleQuantityMode === 'fixed'
        ? componentQuantity
        : componentQuantity * parentQuantity;
    let componentTotal;
    if (discountState?.schemaVersion === BUNDLE_OFFER_SCHEMA_V6) {
      const match = discountState.valid
        ? selectedV6Matches.get(String(input.dataset.bundleVariantId || ''))
        : null;
      const usage = match ? v6DiscountUsage.get(match.usageKey) || 0 : 0;
      componentTotal = calculateBundleRuleComponentTotal({
        priceMinorUnits: input.dataset.bundlePriceMinorUnits,
        quantity: cartQuantity,
        match,
        shopCurrencyCode: root.dataset.currencyCode,
        discountedQuantityUsed: usage,
      });
      if (match && componentTotal.discountedQuantity > 0) {
        v6DiscountUsage.set(match.usageKey, usage + componentTotal.discountedQuantity);
      }
    } else {
      componentTotal = calculateScopedBundleComponentTotal({
        priceMinorUnits: input.dataset.bundlePriceMinorUnits,
        quantity: cartQuantity,
        groupId: input.dataset.bundleGroupId,
        componentDiscountScope: input.dataset.bundleDiscountScope,
        discountActive: offer.dataset.bundleDiscountActive === 'true',
        discountScope: offer.dataset.bundleDiscountScope,
        discountType: offer.dataset.bundleDiscountType,
        discountValue: offer.dataset.bundleDiscountValue,
        discountValueMinorUnits: offer.dataset.bundleDiscountValueMinorUnits,
        discountCurrencyCode: offer.dataset.bundleDiscountCurrencyCode,
        shopCurrencyCode: root.dataset.currencyCode,
        maxDiscountedQuantity: offer.dataset.bundleDiscountMaxQuantity,
        discountedQuantityUsed,
      });
      discountedQuantityUsed += componentTotal.discountedQuantity;
    }
    componentTotalMinorUnits += componentTotal.totalMinorUnits;
    const group = input.closest(GROUP_SELECTOR);
    groupTotals.set(group, (groupTotals.get(group) || 0) + componentTotal.totalMinorUnits);
  });

  return { componentTotalMinorUnits, groupTotals };
};

const syncBundlePurchasePresentation = (root, { animate = false, announce = false } = {}) => {
  const form = root.querySelector('form[action$="/cart/add"]');
  const offer = getOfferElement(root);

  if (!(form instanceof HTMLFormElement) || !(offer instanceof HTMLElement)) {
    return;
  }

  const variant = getCurrentVariant(root);
  const parentUnitPrice = Number(variant?.priceMinorUnits);

  if (!variant || !Number.isFinite(parentUnitPrice)) {
    return;
  }

  const parentQuantity = normalizePositiveInteger(form.querySelector('[data-quantity-input]')?.value);
  const selectedInputs = [...offer.querySelectorAll(`${COMPONENT_SELECTOR}:checked:not(:disabled)`)]
    .filter((input) => input instanceof HTMLInputElement);
  const { componentTotalMinorUnits, groupTotals } = calculateBundleSelectionTotals({
    root,
    offer,
    selectedInputs,
    parentQuantity,
  });
  const totalMinorUnits = parentUnitPrice * parentQuantity + componentTotalMinorUnits;

  offer.querySelectorAll('[data-bundle-group-presentation="compact"]').forEach((group) => {
    const summary = group.querySelector('[data-bundle-compact-summary]');
    if (!summary) return;
    const inputs = [...group.querySelectorAll(COMPONENT_SELECTOR)];
    const available = inputs.filter((input) => !input.disabled).length;
    const chosen = inputs.filter((input) => input.checked && !input.disabled);
    summary.querySelector('[data-bundle-compact-available]').textContent =
      `${available} ${available === 1 ? 'option' : 'options'} available`;
    summary.querySelector('[data-bundle-compact-selected]').textContent = chosen.length
      ? `${chosen.length} selected: ${chosen.map((input) => input.dataset.bundleComponentLabel).join(', ')}`
      : 'No add-ons selected';
    summary.querySelector('[data-bundle-compact-price]').textContent = `+${formatMoney(root, groupTotals.get(group) || 0)}`;
    const triggerLabel = chosen.length ? 'Edit' : 'Choose';
    const trigger = summary.querySelector('[data-bundle-compact-trigger]');
    summary.querySelector('[data-bundle-compact-trigger-label]').textContent = triggerLabel;
    trigger?.setAttribute('aria-label', `${triggerLabel} ${group.querySelector('[data-bundle-group-heading]')?.textContent?.trim() || 'add-ons'}`);
  });

  const formattedTotal = formatMoney(root, totalMinorUnits);
  const selectedCount = selectedInputs.length;
  root.dispatchEvent(
    new CustomEvent('sigma:bundle-purchase-total', {
      detail: { animate, formattedTotal, selectedCount },
    }),
  );

  const status = offer.querySelector('[data-bundle-offer-status]');
  if (announce && status) {
    status.textContent = selectedCount
      ? `${selectedCount === 1 ? 'Add-on' : 'Add-ons'} selected. Total ${formattedTotal}.`
      : `Add-ons cleared. Total ${formattedTotal}.`;
  }
};

const setQuickViewMessage = (body, message, { error = false } = {}) => {
  const paragraph = document.createElement('p');
  paragraph.className = error
    ? 'coffee-product__bundle-quick-view-error'
    : 'coffee-product__bundle-quick-view-loading';
  paragraph.setAttribute('role', error ? 'alert' : 'status');
  paragraph.textContent = message;
  body.replaceChildren(paragraph);
};

const getQuickViewInput = (offer, dialog) => {
  const inputId = dialog.dataset.bundleComponentInputId;
  if (!inputId) return null;

  const staged = bundleCompactStates.get(offer)?.active?.stagedInputs.get(inputId);
  if (staged) return staged;
  return [...offer.querySelectorAll(COMPONENT_SELECTOR)].find(
    (input) => input instanceof HTMLInputElement && input.id === inputId,
  ) || null;
};

const syncQuickViewAction = (offer) => {
  const dialog = offer.querySelector('[data-bundle-quick-view-dialog]');
  const action = dialog?.querySelector('[data-bundle-quick-view-action]');
  const label = action?.querySelector('[data-bundle-quick-view-action-label]');

  if (!(dialog instanceof HTMLDialogElement) || !(action instanceof HTMLButtonElement) || !label) {
    return;
  }

  const input = getQuickViewInput(offer, dialog);
  if (!(input instanceof HTMLInputElement) || dialog.dataset.bundleQuickViewLoaded !== 'true') {
    action.disabled = true;
    label.textContent = 'Add to bundle';
    return;
  }

  if (input.disabled) {
    action.disabled = true;
    label.textContent = 'Currently sold out';
    return;
  }

  if (input.checked) {
    action.disabled = false;
    label.textContent = 'Remove from bundle';
    return;
  }

  const group = input.closest(GROUP_SELECTOR);
  const maximum = normalizePositiveInteger(group?.dataset.bundleGroupMax);
  const selectionSelector = input.matches(STAGED_COMPONENT_SELECTOR) ? STAGED_COMPONENT_SELECTOR : COMPONENT_SELECTOR;
  const selectedCount = group
    ? group.querySelectorAll(`${selectionSelector}:checked:not(:disabled)`).length
    : 0;
  const replacesStagedChoice = input.matches(STAGED_COMPONENT_SELECTOR) && maximum === 1;
  const limitReached = !replacesStagedChoice && selectedCount >= maximum;

  action.disabled = limitReached;
  label.textContent = limitReached ? 'Selection limit reached' : 'Add to bundle';
};

const loadQuickViewContent = async ({ offer, dialog, trigger, body, signal }) => {
  const productUrl = new URL(trigger.dataset.bundleProductUrl, window.location.origin);
  const expectedVariantId = normalizeVariantId(trigger.dataset.bundleVariantId);
  const requestUrl = new URL(productUrl);
  requestUrl.searchParams.set('section_id', QUICK_VIEW_SECTION_ID);

  dialog.dataset.bundleQuickViewLoaded = 'false';
  dialog.dataset.bundleComponentInputId = trigger.dataset.bundleComponentInputId || '';
  dialog.querySelector('[data-bundle-quick-view-full-link]')?.setAttribute('href', productUrl.toString());
  const cacheKey = `${publicViewContext()}\n${requestUrl}`;
  let markup = quickViewCache.get(cacheKey);
  const cacheHit = Boolean(markup);
  if (!markup) setQuickViewMessage(body, 'Loading product details…');
  syncQuickViewAction(offer);
  if (!markup) {
    const response = await fetch(requestUrl, {
      headers: { Accept: 'text/html' },
      signal,
    });
    if (!response.ok) {
      throw new Error(`Product details request failed with ${response.status}.`);
    }
    markup = await response.text();
    if (signal.aborted) return;

  }

  if (signal.aborted) return;

  const template = document.createElement('template');
  template.innerHTML = markup;
  const content = template.content.querySelector('[data-bundle-quick-view-content]');
  if (!(content instanceof HTMLElement)) {
    throw new Error('The product details response was empty.');
  }
  if (normalizeVariantId(content.dataset.bundleQuickViewVariantId) !== expectedVariantId) {
    throw new Error('The product details response did not match the selected add-on.');
  }

  if (signal.aborted) return;

  if (!cacheHit) quickViewCache.set(cacheKey, markup);
  body.replaceChildren(content.cloneNode(true));
  dialog.dataset.bundleQuickViewLoaded = 'true';
  syncQuickViewAction(offer);
};

const initializeQuickView = (offer) => {
  const dialog = offer.querySelector('[data-bundle-quick-view-dialog]');
  const body = dialog?.querySelector('[data-bundle-quick-view-body]');
  const action = dialog?.querySelector('[data-bundle-quick-view-action]');
  const close = dialog?.querySelector('[data-bundle-quick-view-close]');
  const dismiss = dialog?.querySelector('[data-bundle-quick-view-dismiss]');

  if (
    !(dialog instanceof HTMLDialogElement) ||
    !(body instanceof HTMLElement) ||
    !(action instanceof HTMLButtonElement) ||
    typeof dialog.showModal !== 'function'
  ) {
    return false;
  }

  let activeTrigger = null;
  let requestController = null;

  const closeDialog = () => {
    closeBundleDialog(dialog);
  };

  offer.addEventListener('click', (event) => {
    const trigger = event.target.closest(QUICK_VIEW_TRIGGER_SELECTOR);
    if (!(trigger instanceof HTMLButtonElement) || !offer.contains(trigger)) return;

    event.preventDefault();
    activeTrigger?.setAttribute('aria-expanded', 'false');
    activeTrigger = trigger;
    activeTrigger.setAttribute('aria-expanded', 'true');
    requestController?.abort();
    const controller = new AbortController();
    requestController = controller;

    const compact = bundleCompactStates.get(offer);
    if (compact?.active?.dialog.open && compact.active.dialog.contains(trigger)) {
      compact.active.suspended = true;
      compact.active.ignoreNextClose = true;
      compact.active.dialog.close();
    }
    openBundleDialog(dialog);
    close?.focus({ preventScroll: true });

    void loadQuickViewContent({
      offer,
      dialog,
      trigger,
      body,
      signal: controller.signal,
    }).catch((error) => {
      if (controller.signal.aborted || requestController !== controller) return;
      dialog.dataset.bundleQuickViewLoaded = 'false';
      setQuickViewMessage(
        body,
        error instanceof Error
          ? 'Product details could not be loaded. You can still open the full product page.'
          : 'Product details could not be loaded.',
        { error: true },
      );
      syncQuickViewAction(offer);
    });
  });

  close?.addEventListener('click', closeDialog);
  dismiss?.addEventListener('click', closeDialog);
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    closeDialog();
  });
  dialog.addEventListener('close', () => {
    resetBundleDialogState(dialog);
    requestController?.abort();
    activeTrigger?.setAttribute('aria-expanded', 'false');
    const compact = bundleCompactStates.get(offer);
    if (compact?.active?.suspended) {
      compact.active.suspended = false;
      openBundleDialog(compact.active.dialog);
    }
    activeTrigger?.focus({ preventScroll: true });
    activeTrigger = null;
  });

  action.addEventListener('click', (event) => {
    const input = getQuickViewInput(offer, dialog);
    if (!(input instanceof HTMLInputElement) || input.disabled) return;

    input.checked = !input.checked;
    input.dispatchEvent(
      new CustomEvent('change', {
        bubbles: true,
        detail: { animate: event.detail > 0 },
      }),
    );

    if (input.checked || action.querySelector('[data-bundle-quick-view-action-label]')?.textContent === 'Remove from bundle') {
      closeDialog();
    } else {
      syncQuickViewAction(offer);
    }
  });

  return true;
};

const clearBundleLimitDescription = (control) => {
  const statusId = control.dataset.bundleLimitStatusId;
  if (!statusId) return;

  const describedBy = (control.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter((id) => id && id !== statusId);
  if (describedBy.length) control.setAttribute('aria-describedby', describedBy.join(' '));
  else control.removeAttribute('aria-describedby');
  delete control.dataset.bundleLimitStatusId;
};

const enforceGroupLimit = (input) => {
  const group = input.closest(GROUP_SELECTOR);
  if (!(group instanceof HTMLElement) || !input.checked) {
    return true;
  }

  const selectionSelector = input.matches(STAGED_COMPONENT_SELECTOR) ? STAGED_COMPONENT_SELECTOR : COMPONENT_SELECTOR;
  const selectedInputs = [...group.querySelectorAll(`${selectionSelector}:checked:not(:disabled)`)]
    .filter((candidate) => candidate instanceof HTMLInputElement);
  const maximum = normalizePositiveInteger(group.dataset.bundleGroupMax);

  if (maximum === 1 && selectedInputs.length > 1) {
    selectedInputs.forEach((candidate) => {
      if (candidate !== input) candidate.checked = false;
    });
    return true;
  }

  const result = reconcileGroupSelection({
    selectedIds: selectedInputs.map((candidate) => candidate.id),
    changedId: input.id,
    maxSelections: group.dataset.bundleGroupMax,
  });

  if (result.accepted) return true;

  input.checked = false;
  const isStagedInput = input.matches(STAGED_COMPONENT_SELECTOR);
  const status = isStagedInput
    ? group.querySelector('[data-bundle-compact-status]')
    : group.querySelector('[data-bundle-group-status]');
  const target = input.closest('.coffee-product__bundle-component');
  if (status) {
    status.textContent = 'Limit reached. Remove one add-on first.';
    status.classList.add('is-error');
    group.querySelectorAll('[data-bundle-limit-status-id]').forEach((control) => {
      if (control !== input) clearBundleLimitDescription(control);
    });
    const describedBy = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    describedBy.add(status.id);
    input.setAttribute('aria-describedby', [...describedBy].join(' '));
    input.dataset.bundleLimitStatusId = status.id;
  }
  if (target instanceof HTMLElement) {
    const state = bundleLimitFeedbackStates.get(group) || {};
    const shouldPlayErrorTone = !state.soundTimer;
    if (state.shakeTimer) window.clearTimeout(state.shakeTimer);
    if (state.soundTimer) window.clearTimeout(state.soundTimer);
    if (state.target && state.target !== target) {
      state.target.classList.remove('is-limit-error', 'is-limit-shaking');
    }
    target.classList.add('is-limit-error');
    target.classList.remove('is-limit-shaking');
    void target.offsetWidth;
    target.classList.add('is-limit-shaking');
    state.target = target;
    state.shakeTimer = window.setTimeout(() => {
      target.classList.remove('is-limit-shaking');
      state.shakeTimer = null;
    }, BUNDLE_LIMIT_FEEDBACK_DURATION);
    state.soundTimer = window.setTimeout(() => {
      state.soundTimer = null;
    }, BUNDLE_LIMIT_FEEDBACK_DURATION);
    bundleLimitFeedbackStates.set(group, state);
    if (shouldPlayErrorTone) {
      try {
        window.SigmaThemeAudio?.playError();
      } catch {
        // Audio feedback is optional and must never interrupt bundle selection.
      }
    }
  }
  return false;
};

const clearGroupLimitFeedback = (group) => {
  if (!(group instanceof HTMLElement)) return;

  const state = bundleLimitFeedbackStates.get(group);
  if (state?.shakeTimer) window.clearTimeout(state.shakeTimer);
  if (state?.soundTimer) window.clearTimeout(state.soundTimer);
  bundleLimitFeedbackStates.delete(group);
  group.querySelectorAll('.is-limit-error, .is-limit-shaking').forEach((target) => {
    target.classList.remove('is-limit-error', 'is-limit-shaking');
  });
  group.querySelectorAll('[data-bundle-limit-status-id]').forEach(clearBundleLimitDescription);
  group.querySelectorAll('[data-bundle-group-status], [data-bundle-compact-status]').forEach((status) => {
    status.textContent = '';
    status.classList.remove('is-error');
  });
};

const syncCompactDraft = ({ root, offer, entry }) => {
  const form = root.querySelector('form[action$="/cart/add"]');
  if (!(form instanceof HTMLFormElement)) return;

  const parentQuantity = normalizePositiveInteger(form.querySelector('[data-quantity-input]')?.value);
  const simulatedInputs = [...offer.querySelectorAll(COMPONENT_SELECTOR)]
    .map((input) => input.closest(GROUP_SELECTOR) === entry.group
      ? entry.stagedInputs.get(input.id)
      : input)
    .filter((input) => input instanceof HTMLInputElement && input.checked && !input.disabled);
  const { groupTotals } = calculateBundleSelectionTotals({
    root,
    offer,
    selectedInputs: simulatedInputs,
    parentQuantity,
  });
  const count = [...entry.stagedInputs.values()]
    .filter((input) => input.checked && !input.disabled)
    .length;
  const draft = entry.dialog.querySelector('[data-bundle-compact-draft]');
  if (draft) {
    draft.textContent = `${count} selected · +${formatMoney(root, groupTotals.get(entry.group) || 0)}`;
  }
};

// Staged cards never belong to the purchase form or the committed component selector.
const initializeCompactChoosers = (root, offer) => {
  const state = { active: null, groups: [] };
  bundleCompactStates.set(offer, state);
  offer.querySelectorAll('[data-bundle-group-presentation="compact"]').forEach((group) => {
    const dialog = group.querySelector('[data-bundle-compact-dialog]');
    const trigger = group.querySelector('[data-bundle-compact-trigger]');
    const summary = group.querySelector('[data-bundle-compact-summary]');
    const source = group.querySelector(':scope > .coffee-product__bundle-components');
    const choices = dialog?.querySelector('[data-bundle-compact-choices]');
    if (!(dialog instanceof HTMLDialogElement) || !trigger || !summary || !source || !choices) return;
    const entry = { group, dialog, trigger, summary, source, choices, stagedInputs: new Map(), suspended: false };
    state.groups.push(entry);

    trigger.addEventListener('click', () => {
      if (state.active || root.dataset.bundleOfferEnhanced !== 'true') return;
      clearGroupLimitFeedback(group);
      choices.replaceChildren();
      entry.stagedInputs.clear();
      [...source.children].forEach((card) => {
        const clone = card.cloneNode(true);
        // Remap every ID, including support-copy references, to avoid duplicate document IDs.
        clone.querySelectorAll('[id]').forEach((element) => { element.id += '-staged'; });
        clone.querySelectorAll('[for]').forEach((element) => {
          element.setAttribute('for', `${element.getAttribute('for')}-staged`);
        });
        clone.querySelectorAll('[aria-describedby]').forEach((element) => {
          element.setAttribute('aria-describedby', element.getAttribute('aria-describedby').split(/\s+/).map((id) => `${id}-staged`).join(' '));
        });
        const original = card.querySelector(COMPONENT_SELECTOR);
        const input = clone.querySelector(COMPONENT_SELECTOR);
        input.removeAttribute('name');
        // Empty explicit form ownership prevents implicit association inside the purchase form.
        input.setAttribute('form', '');
        input.removeAttribute('data-bundle-component');
        input.removeAttribute('data-bundle-enhancement-control');
        input.setAttribute('data-bundle-compact-stage-input', '');
        input.checked = original.checked;
        input.disabled = original.disabled;
        entry.stagedInputs.set(original.id, input);
        clone.removeAttribute('data-bundle-component-card');
        clone.style.removeProperty('--bundle-component-card-height');
        choices.append(clone);
      });
      state.active = entry;
      syncCompactDraft({ root, offer, entry });
      trigger.setAttribute('aria-expanded', 'true');
      openBundleDialog(dialog);
      dialog.querySelector('[data-bundle-compact-cancel][type="button"]')?.focus({ preventScroll: true });
    });

    const discard = () => {
      clearGroupLimitFeedback(group);
      closeBundleDialog(dialog);
    };
    dialog.querySelectorAll('[data-bundle-compact-cancel]').forEach((control) => control.addEventListener('click', discard));
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); discard(); });
    dialog.addEventListener('close', () => {
      resetBundleDialogState(dialog);
      if (entry.ignoreNextClose) {
        entry.ignoreNextClose = false;
        return;
      }
      clearGroupLimitFeedback(group);
      state.active = null;
      entry.stagedInputs.clear();
      choices.replaceChildren();
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({ preventScroll: true });
    });
    dialog.addEventListener('change', (event) => {
      if (!(event.target instanceof HTMLInputElement) || !event.target.matches(STAGED_COMPONENT_SELECTOR)) return;
      event.stopPropagation();
      if (enforceGroupLimit(event.target)) clearGroupLimitFeedback(group);
      syncCompactDraft({ root, offer, entry });
      syncQuickViewAction(offer);
    });
    dialog.querySelector('[data-bundle-compact-apply]').addEventListener('click', () => {
      if (state.active !== entry || entry.suspended || dialog.dataset.bundleDialogState === 'closing') return;
      const originals = [...source.querySelectorAll(COMPONENT_SELECTOR)];
      const selected = [...entry.stagedInputs.values()].filter((input) => input.checked && !input.disabled);
      if (selected.length > normalizePositiveInteger(group.dataset.bundleGroupMax)) return;
      clearGroupLimitFeedback(group);
      originals.forEach((input) => {
        input.checked = !input.disabled && entry.stagedInputs.get(input.id)?.checked === true;
      });
      // One controller event after the entire draft is committed, including an empty selection.
      originals[0]?.dispatchEvent(new CustomEvent('change', { bubbles: true, detail: { animate: true } }));
      closeBundleDialog(dialog);
    });
  });
  return state;
};

const initializeBundleOffer = (root) => {
  const offer = getOfferElement(root);
  if (root.dataset.bundleOfferEnhanced || !(offer instanceof HTMLElement)) {
    return;
  }

  root.dataset.bundleOfferEnhanced = 'initializing';
  setBundleEnhancementControlsEnabled(offer, false);
  let pointerActivatedControl = null;
  const syncAfterCurrentEvent = (options) => {
    queueMicrotask(() => syncBundlePurchasePresentation(root, options));
  };

  try {
    if (!initializeQuickView(offer)) {
      throw new Error('Bundle enhancement requires supported native dialog controls.');
    }
    initializeBundleDiscountPresentation(root, offer);
    initializeBundleCardLayout(offer);
    const syncReadyNowRefresh = initializeReadyNowRefresh(offer);
    const compact = initializeCompactChoosers(root, offer);

    root.addEventListener('pointerdown', (event) => {
      const input = event.target instanceof Element
        ? event.target.closest('label')?.control || event.target.closest('input')
        : null;
      pointerActivatedControl = input instanceof HTMLInputElement ? input : null;
    }, true);

    root.addEventListener('change', (event) => {
      if (event.target.matches(COMPONENT_SELECTOR)) {
        const shouldAnimate =
          pointerActivatedControl === event.target || event.detail?.animate === true;
        pointerActivatedControl = null;
        const accepted = enforceGroupLimit(event.target);
        if (accepted) {
          const group = event.target.closest(GROUP_SELECTOR);
          clearGroupLimitFeedback(group);
        }
        syncQuickViewAction(offer);
        syncReadyNowRefresh();
        syncAfterCurrentEvent({ animate: shouldAnimate && accepted, announce: accepted });
        return;
      }

      if (event.target.matches('[data-option-input], [data-variant-select]')) {
        syncAfterCurrentEvent({ animate: true });
      }
    });

    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-quantity-input]')) {
        syncAfterCurrentEvent();
      }
    });

    root.addEventListener('sigma:product-quantity-change', () => syncAfterCurrentEvent({ animate: true }));
    setBundleEnhancementControlsEnabled(offer, true);
    syncReadyNowRefresh();
    root.dataset.bundleOfferEnhanced = 'true';
    compact.groups.forEach(({ summary, source }) => {
      summary.hidden = false;
      source.hidden = true;
    });
    // Keep Shopify's variant radios in place; hide only controls replaced by a ready chooser.
    root.querySelectorAll('[data-bundle-replaced-option]').forEach((option) => {
      if (!compact.groups.some(({ group }) => group.dataset.bundleGroupId === option.dataset.bundleReplacedOption)) return;
      option.hidden = true;
      root.querySelectorAll('[data-sticky-selected-option]').forEach((selectedOption) => {
        if (selectedOption.dataset.stickySelectedOptionPosition === option.dataset.optionPosition) {
          selectedOption.hidden = true;
        }
      });
    });
    syncAfterCurrentEvent();
  } catch (error) {
    setBundleEnhancementControlsEnabled(offer, false);
    root.dataset.bundleOfferEnhanced = 'failed';
    const status = offer.querySelector('[data-bundle-offer-status]');
    if (status) {
      status.textContent = 'Add-ons are temporarily unavailable. You can still add the main product.';
    }
    console.error('Bundle enhancement failed safely.', error);
  }
};

export const initializeBundleOffers = (scope = document) => {
  scope.querySelectorAll(PRODUCT_SELECTOR).forEach(initializeBundleOffer);
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeBundleOffers(), { once: true });
  } else {
    initializeBundleOffers();
  }

  document.addEventListener('shopify:section:load', (event) => initializeBundleOffers(event.target));
}
